import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

/** Кнопка в тосте — «Отменить» после быстрой правки в таблице. */
export type ToastAction = {
  label: string
  onAction: () => void
}

type ToastItem = {
  id: number
  message: string
  action?: ToastAction
}

type ToastContextValue = {
  showToast: (message: string, action?: ToastAction) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(0)

  const showToast = useCallback((message: string, action?: ToastAction) => {
    const id = nextId.current + 1
    nextId.current = id
    setToasts((current) => [...current, { id, message, action }])
  }, [])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  useEffect(() => {
    if (toasts.length === 0) {
      return
    }
    const timer = window.setTimeout(() => {
      setToasts((current) => current.slice(1))
    }, toasts[0].action ? 5000 : 2800)
    return () => window.clearTimeout(timer)
  }, [toasts])

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="market-toast-stack" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`market-toast${toast.action ? ' market-toast--action' : ''}`}
            role="status"
          >
            <span className="market-toast__message">{toast.message}</span>
            {toast.action ? (
              <button
                type="button"
                className="market-toast__action"
                onClick={() => {
                  toast.action?.onAction()
                  dismiss(toast.id)
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
            <button
              type="button"
              className="market-toast__dismiss"
              aria-label="Закрыть уведомление"
              onClick={() => dismiss(toast.id)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="m3 3 10 10M13 3 3 13" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}
